/* PPE Parts & Supply: product page scripts.
   <product-gallery data-section-id>: [data-media-slide][data-media-id], button[data-media-thumb][data-media-id],
     [data-product-badge] (data-show-sale, data-suffix, data-sold-out). Follows `variant:change` from the
     <product-info> with the same data-section-id and toggles [data-buy-row] by availability.
   <product-recommendations data-url>: [data-recommendations-inner], [data-recommendations-grid],
     template[data-recommendations-fallback]. */
(function () {
  'use strict';

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  if (!customElements.get('product-gallery')) {
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound) return;
        this.dataset.bound = 'true';

        this.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-media-thumb]');
          if (thumb && this.contains(thumb)) this.show(thumb.dataset.mediaId);
        });

        this.onVariantChange = (event) => {
          const source = event.target;
          if (!source || !source.dataset || source.dataset.sectionId !== this.dataset.sectionId) return;
          const variant = event.detail && event.detail.variant;
          if (!variant) return;
          if (variant.featured_media && variant.featured_media.id) this.show(variant.featured_media.id);
          this.updateBadge(variant);
          source.querySelectorAll('[data-buy-row]').forEach((row) => { row.hidden = !variant.available; });
        };
        document.addEventListener('variant:change', this.onVariantChange);
      }

      disconnectedCallback() {
        document.removeEventListener('variant:change', this.onVariantChange);
        delete this.dataset.bound;
      }

      show(id) {
        const target = String(id);
        const slides = Array.from(this.querySelectorAll('[data-media-slide]'));
        if (!slides.some((slide) => slide.dataset.mediaId === target)) return;

        slides.forEach((slide) => {
          const active = slide.dataset.mediaId === target;
          slide.hidden = !active;
          if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
        });
        this.querySelectorAll('[data-media-thumb]').forEach((thumb) => {
          thumb.setAttribute('aria-pressed', String(thumb.dataset.mediaId === target));
        });
      }

      updateBadge(variant) {
        const badge = this.querySelector('[data-product-badge]');
        if (!badge) return;
        if (!variant.available) {
          badge.textContent = badge.dataset.soldOut || '';
          badge.classList.add('badge--dark');
          badge.hidden = false;
          return;
        }
        badge.classList.remove('badge--dark');
        if (badge.dataset.showSale === 'true' && variant.compare_at_price > variant.price) {
          const percent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          badge.textContent = `−${percent}% ${badge.dataset.suffix || ''}`.trim();
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }
    }
    customElements.define('product-gallery', ProductGallery);
  }

  if (!customElements.get('product-recommendations')) {
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound) return;
        this.dataset.bound = 'true';
        if (!this.dataset.url) return;

        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        }, { rootMargin: '0px 0px 400px 0px' });
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        delete this.dataset.bound;
      }

      async load() {
        const grid = this.querySelector('[data-recommendations-grid]');
        const inner = this.querySelector('[data-recommendations-inner]');
        if (!grid || !inner) return;

        let html = '';
        try {
          const response = await fetch(this.dataset.url);
          if (response.ok) {
            const fresh = parseHTML(await response.text()).querySelector('[data-recommendations-grid]');
            if (fresh && fresh.querySelector('.product-card')) html = fresh.innerHTML;
          }
        } catch (error) {
          /* offline, fall through to fallback */
        }

        /* The grid is already server-rendered with the fallback collection, so
           an empty response means keep what is on the page, not blank it. */
        if (!html) {
          if (!grid.querySelector('.product-card')) this.hidden = true;
          return;
        }
        grid.innerHTML = html;
        inner.hidden = false;
      }
    }
    customElements.define('product-recommendations', ProductRecommendations);
  }
})();
